import React from 'react';

import { FormInputProps } from '@occc-app/design-system';
import { Typography } from '@occc/ui-design-system';
import { FormInput } from '@occc-app/Offer/components/FormInput';

import {
  greaterThanZeroValidation,
  numberValidation,
  FormEventValidationCheck,
  withFormEventValidation,
} from '../../validatableComponent';
import { FormCheckbox } from '../../FormCheckbox';
import { actions, State } from '../redux';

type MaxAgeValidateWith = Pick<State['ageWithin'], 'minimumAge' | 'minimumAgeActive'>;
const maxAgeAfterMinValidation: FormEventValidationCheck<MaxAgeValidateWith> = (
  event,
  validateWith,
) => {
  if (!validateWith) {
    throw new Error('maxAgeAfterMinValidation requires validateWith');
  }

  return {
    isValid:
      !validateWith.minimumAgeActive ||
      parseInt(event.currentTarget.value, 10) >= Number(validateWith.minimumAge),
    validationError: 'Maximum age must be greater than maximum age',
  };
};

const MaxAgeInput = withFormEventValidation<FormInputProps, MaxAgeValidateWith>(
  FormInput,
  numberValidation,
  greaterThanZeroValidation,
  maxAgeAfterMinValidation,
);

type MinAgeValidateWith = Pick<State['ageWithin'], 'maximumAge' | 'maximumAgeActive'>;
const minAgeBeforeMaxValidation: FormEventValidationCheck<MinAgeValidateWith> = (
  event,
  validateWith,
) => {
  if (!validateWith) {
    throw new Error('minAgeBeforeMaxValidation requires validateWith');
  }

  return {
    isValid:
      !!validateWith &&
      (!validateWith.maximumAgeActive ||
        parseInt(event.currentTarget.value, 10) <= (validateWith.maximumAge as number)),
    validationError: 'Minimum age must be less than maximum age',
  };
};

const MinAgeInput = withFormEventValidation<FormInputProps, MinAgeValidateWith>(
  FormInput,
  numberValidation,
  greaterThanZeroValidation,
  minAgeBeforeMaxValidation,
);

type Actions = typeof actions;
type ComponentState = State['ageWithin'];
type Fields = keyof ComponentState;
export type Props = ComponentState & {
  audienceConfigurationUpdate: Actions['audienceConfigurationUpdate'];
  editMode?: boolean;
  fieldDisabled: (field: Fields) => boolean;
  readOnly?: boolean;
};
const AgeWithinConfiguration: React.FC<Props> = (props) => {
  const {
    audienceConfigurationUpdate,
    editMode,
    fieldDisabled,
    maximumAge,
    maximumAgeActive,
    minimumAge,
    minimumAgeActive,
    readOnly,
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: boolean | number | string = event.currentTarget.value;
    value =
      event.currentTarget.type.toLowerCase() === 'checkbox'
        ? event.currentTarget.checked
        : parseInt(value, 10);

    audienceConfigurationUpdate('ageWithin', event.currentTarget.id, value);
  };

  const formLabelComponent = !readOnly && (
    <Typography paragraph>Specify minimum and/or maximum ages:</Typography>
  );

  const renderMinimumAgeComponent = () => {
    if (readOnly && !(readOnly && minimumAgeActive)) {
      return;
    }

    const minimumAgeCheckbox = !readOnly && !editMode && (
      <FormCheckbox
        id='minimumAgeActive'
        label='Minimum Age'
        onChange={handleChange}
        disabled={fieldDisabled('minimumAgeActive')}
        checked={minimumAgeActive}
      />
    );

    const minimumAgeInput = minimumAgeActive && (
      <MinAgeInput
        id='minimumAge'
        label='Minimum Age'
        onChange={handleChange}
        readonly={fieldDisabled('minimumAge')}
        type='text'
        value={minimumAge}
        validateWith={{ maximumAge, maximumAgeActive }}
      />
    );

    return (
      <>
        {minimumAgeCheckbox}
        {minimumAgeInput}
      </>
    );
  };

  const renderMaximumAgeComponent = () => {
    if (readOnly && !(readOnly && maximumAgeActive)) {
      return;
    }

    const maximumAgeCheckbox = !readOnly && !editMode && (
      <FormCheckbox
        id='maximumAgeActive'
        label='Maximum Age'
        onChange={handleChange}
        disabled={fieldDisabled('maximumAgeActive')}
        checked={maximumAgeActive}
      />
    );

    const maximumAgeInput = maximumAgeActive && (
      <MaxAgeInput
        id='maximumAge'
        label='Maximum Age'
        onChange={handleChange}
        readonly={fieldDisabled('maximumAge')}
        type='number'
        value={maximumAge}
        validateWith={{ minimumAge, minimumAgeActive }}
      />
    );

    return (
      <>
        {maximumAgeCheckbox}
        {maximumAgeInput}
      </>
    );
  };

  return (
    <>
      {formLabelComponent}
      {renderMinimumAgeComponent()}
      {renderMaximumAgeComponent()}
    </>
  );
};

export { AgeWithinConfiguration };
